import { useOne } from "@refinedev/core";
import { Link, useParams } from "react-router";

import { Badge } from "@/components/Badge";
import {
  BackLink,
  Card,
  DefinitionList,
  ErrorBox,
  Loading,
  PageHeader,
} from "@/components/Page";
import { formatDateTime, shipmentKind, shipmentStatus } from "@/lib/format";

interface ShipmentEvent {
  id: string;
  status: string;
  occurred_at: string;
}

interface ShipmentDetail extends Record<string, unknown> {
  id: string;
  kind: string;
  status: string;
  service: string;
  provider: string;
  tracking_number: string | null;
  target_point: string | null;
  shipx_shipment_id: string | null;
  label_ready: boolean;
  receiver_name: string | null;
  order_id: string | null;
  created_at: string;
  delivered_at: string | null;
  events: ShipmentEvent[];
}

export function ShipmentShowPage() {
  const { id = "" } = useParams();
  const { query } = useOne<ShipmentDetail>({ resource: "shipments", id });
  const shipment = query.data?.data;

  if (query.isLoading) return <Loading />;
  if (query.error) return <ErrorBox error={query.error} />;
  if (!shipment) return null;

  const status = shipmentStatus(shipment.status);

  return (
    <>
      <BackLink to="/przesylki">Przesyłki</BackLink>
      <PageHeader
        title={`Przesyłka: ${shipmentKind(shipment.kind)}`}
        description={shipment.tracking_number ?? undefined}
        actions={<Badge tone={status.tone}>{status.label}</Badge>}
      />

      <div className="grid items-start gap-4.5 xl:grid-cols-2">
        <Card title="Dane">
          <DefinitionList
            rows={[
              ["Usługa", shipment.service],
              ["Dostawca", shipment.provider],
              ["Identyfikator ShipX", shipment.shipx_shipment_id ?? "-"],
              ["Punkt odbioru", shipment.target_point ?? "-"],
              ["Odbiorca", shipment.receiver_name ?? "-"],
              ["Etykieta", shipment.label_ready ? "gotowa" : "jeszcze nie"],
              ["Utworzona", formatDateTime(shipment.created_at)],
              ["Doręczona", formatDateTime(shipment.delivered_at)],
              [
                "Zamówienie",
                shipment.order_id ? (
                  <Link
                    to={`/zamowienia/${shipment.order_id}`}
                    className="text-accent underline underline-offset-4 hover:text-accent-dark"
                  >
                    Otwórz zamówienie
                  </Link>
                ) : (
                  "-"
                ),
              ],
            ]}
          />
        </Card>

        <Card title="Historia statusów">
          {shipment.events.length === 0 ? (
            <p className="text-base text-ink-soft">
              Brak zdarzeń. Statusy zaczną tu wpadać razem z odpytywaniem trackingu ShipX.
            </p>
          ) : (
            <ol className="flex flex-col gap-2.5 text-base">
              {shipment.events.map((event) => (
                <li key={event.id} className="flex justify-between gap-4">
                  <span className="text-ink">{shipmentStatus(event.status).label}</span>
                  <span className="text-ink-soft">{formatDateTime(event.occurred_at)}</span>
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>
    </>
  );
}
